import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { ILineGroup, ILineResource } from '../../entities/farms.entities';
import { sendSingleRequest } from '../../apis';
import { showFeedback } from '../../store/ui/ui.actions';
import { useHistory } from 'react-router-dom';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { loadFarmsData } from '../../store/farms/farms.actions';
import { Button, Dropdown, Input, Title } from '../../components/shared';
import { useWidth } from '../../lib/useWidth';
import { selectFarmingMethods } from '../../store/utils/utils.selector';
import LineLocationModal from '../../components/shared/modal/LineLocationModal';
import {
  ILocation,
  TBusinessType,
  TLineType,
} from '../../entities/general.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { translate, transUnit } from '../../lib/lang.helper';
import { selectInventories } from '../../store/inventories/inventories.selector';
import SectionLocationForm from '../../components/shared/modal/SectionLocationForm';
import SelectCropType from '../../components/farmform/SelectCropType';
import { numToStr } from '../../lib/common.helpers';

const mostCommonCrop = (lines: ILineResource[]) => {
  if (lines.length <= 0) {
    return 'MUSSEL';
  }
  const counts = lines.reduce(
    (acc, line) => {
      const cropType = line.crop_type;
      acc[cropType] = (acc[cropType] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const mostCommon = Object.entries(counts).reduce((a, b) =>
    a[1] > b[1] ? a : b,
  )[0] as TBusinessType;

  return mostCommon;
};

interface IFormData {
  crop_type: TBusinessType;
  line_type: TLineType;
  line_name: string;
  backbone?: number | null;
  width?: number | null;
  length?: number | null;
  points: Array<{ lat: number | null; lng: number | null }> | null;
  line_group_id?: number | null;
  basket_count?: number | null;
  tank_area?: number | null;
  farming_method?: string;
  inventory_id?: number | null;
}

const AddLines = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const params = useParams<{ idFarm: string }>();
  const width = useWidth();

  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const curFarm = farmsData.find(x => x.id === Number(params.idFarm));
  const farmingMethods = useSelector(selectFarmingMethods);
  const inventories = useSelector(selectInventories);

  const [formData, setFormData] = useState<IFormData>({
    line_name: `${(curFarm?.lines.length ?? 0) + 1}`,
    line_type: curFarm?.structure === 'RAFT' ? 'DROPPERS' : 'LONGLINE',
    crop_type: mostCommonCrop(curFarm?.lines ?? []),
    points: null,
  });
  const [disabled, setDisabled] = useState(false);
  const [lineGroups, setLineGroups] = useState<ILineGroup[]>([]);
  const [visibleMap, setVisibleMap] = useState(false);

  const handleChangePoints = (i: number, x: 'lat' | 'lng', v: string) => {
    let points = formData.points ? [...formData.points] : [];
    while (points.length <= i) points.push({ lat: null, lng: null });
    points[i][x] = v.length <= 0 ? null : Number(v);
    setFormData(prv => ({ ...prv, points }));
  };
  const changeLineType = (type: TLineType) => {
    let tmp = { ...formData, line_type: type };
    if (type === 'LONGLINE' || type === 'DROPPERS') {
      tmp.width = tmp.length = undefined;
    } else {
      tmp.backbone = undefined;
    }
    setFormData(tmp);
  };
  const updateForm = (key: keyof IFormData, val: any) => {
    if (
      ['backbone', 'width', 'length', 'basket_count', 'tank_area'].includes(key)
    ) {
      val = val.length <= 0 ? null : Number(val);
    }
    if (key === 'farming_method') {
      const ivt = inventories.find(x => x.type === val);
      let tmp = { ...formData, farming_method: val };
      if (ivt) {
        tmp.inventory_id = ivt.id;
      }
      setFormData(tmp);
    } else {
      setFormData(prv => ({ ...prv, [key]: val }));
    }
  };
  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessage: true,
        type: 'error',
        message: translate(lang, message),
      }),
    );

  const handleConfirmData = async () => {
    let data = { farm_id: curFarm?.id, ...formData };
    if (!data.line_name) {
      showError('_fill_field_name');
      return;
    }
    if (data.crop_type === 'MUSSEL') {
      if (data.line_type === 'PLATFORM') {
        if (!data.width) {
          showError('Please input valid capacity width');
          return;
        }
        if (!data.length) {
          showError('Please input valid capacity length');
          return;
        }
      } else {
        if (!data.backbone) {
          showError(
            translate(
              lang,
              'Please input valid %s',
              transUnit(lang, 'backbone', curFarm?.structure),
            ),
          );
          return;
        }
        if (curFarm?.structure === 'RAFT' && !data.basket_count) {
          showError(
            translate(
              lang,
              'Please input valid %s',
              transUnit(lang, 'basket_count', curFarm.structure),
            ),
          );
          return;
        }
      }
    } else if (data.crop_type === 'OYSTER') {
      if (!data.farming_method) {
        showError('_sel_farming_method');
        return;
      }
      if (!data.basket_count) {
        showError('_input_valid_basket_count');
        return;
      }
    } else {
      if (!data.tank_area) {
        showError('_input_valid_tank_area');
        return;
      }
    }
    if (
      data.points &&
      ((curFarm?.structure === 'RAFT' && data.points.length !== 4) ||
        (curFarm?.structure !== 'RAFT' && data.points.length !== 2) ||
        data.points.some(x => x.lat === null || x.lng === null))
    ) {
      showError('_input_valid_location_points');
      return;
    }

    setDisabled(true);
    const res = await sendSingleRequest(
      data,
      'POST',
      'api/farm/line/lines',
      true,
    );
    if (!res.status) {
      showError(res.data?.message ?? 'Unknown error');
      setDisabled(false);
    } else {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(
            lang,
            res.data?.message ?? '_line_added_ok',
          ),
        }),
      );
      history.replace(`/farms/${curFarm?.id}`);
    }
  };

  useEffect(() => {
    if (curFarm) {
      sendSingleRequest(
        { farm_id: curFarm.id },
        'GET',
        'api/line-group',
        true,
      ).then(res => {
        if (res.status) {
          setLineGroups(res.data);
        }
      });
    }
  }, [curFarm]);

  const groupOptions = [
    { id: '0', value: '0', label: `( ${translate(lang, 'None')} )` },
    ...lineGroups.map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.name,
    })),
  ];

  return (
    <div className='h-calc-80 bg-main'>
      <div className='container w-100'>
        <div className='mt-37 d-flex justify-center'>
          <div className='content'>
            <Title
              className='mb-16'
              size={5}
              color='black'
              align='default'
              fontWeight={700}
            >
              {transUnit(lang, 'line_details', curFarm?.structure)}
            </Title>
            <Input
              label={transUnit(lang, 'line_name', curFarm?.structure)}
              type='text'
              className='mb-16'
              value={formData.line_name}
              onChange={e => updateForm('line_name', e.target.value)}
            />
            <div className='mb-17'>
              <SelectCropType
                value={formData.crop_type}
                onChange={v => updateForm('crop_type', v)}
              />
            </div>
            {formData.crop_type === 'MUSSEL' ? (
              <>
                <div className='mb-17'>
                  <Dropdown
                    label={translate(lang, 'Line type')}
                    options={[
                      { id: 'L', value: 'LONGLINE', label: 'Longline' },
                      { id: 'D', value: 'DROPPERS', label: 'Droppers' },
                      { id: 'P', value: 'PLATFORM', label: 'Platform' },
                    ]}
                    value={formData.line_type}
                    onChange={v => changeLineType(v as any)}
                    disabled={curFarm?.structure === 'RAFT'}
                  />
                </div>
                {formData.line_type === 'PLATFORM' ? (
                  <div className='d-flex mb-17'>
                    <div className='w-100 mr-12'>
                      <Input
                        label={translate(lang, 'Capacity width')}
                        type='number'
                        value={numToStr(formData.width)}
                        onChange={e => updateForm('width', e.target.value)}
                      />
                    </div>
                    <div className='w-100 ml-12'>
                      <Input
                        label={translate(lang, 'Capacity length')}
                        type='number'
                        value={numToStr(formData.length)}
                        onChange={e => updateForm('length', e.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className='mb-17'>
                    <Input
                      label={transUnit(lang, 'backbone', curFarm?.structure)}
                      unit='m'
                      type='number'
                      value={numToStr(formData.backbone)}
                      onChange={e => updateForm('backbone', e.target.value)}
                    />
                  </div>
                )}
                {curFarm?.structure === 'RAFT' && (
                  <div className='mb-17'>
                    <Input
                      label={transUnit(
                        lang,
                        'basket_count',
                        curFarm?.structure,
                      )}
                      type='number'
                      value={numToStr(formData.basket_count)}
                      onChange={e => updateForm('basket_count', e.target.value)}
                    />
                  </div>
                )}
              </>
            ) : formData.crop_type === 'OYSTER' ? (
              <>
                <div className='mb-16'>
                  <Input
                    label={translate(lang, 'Length')}
                    unit='m'
                    type='number'
                    value={numToStr(formData.backbone)}
                    onChange={e => updateForm('backbone', e.target.value)}
                  />
                </div>
                <div className='mb-17'>
                  <Dropdown
                    label={translate(lang, 'Farming method')}
                    options={farmingMethods.map(x => ({
                      id: x.name,
                      value: x.name,
                      label: x.name,
                    }))}
                    value={formData.farming_method}
                    onChange={v => updateForm('farming_method', v)}
                  />
                </div>
                {inventories.length > 0 && (
                  <div className='mb-17'>
                    <Dropdown
                      label={translate(lang, 'Default inventory')}
                      options={inventories.map(x => ({
                        id: x.id.toString(),
                        value: x.id.toString(),
                        label: `${x.type} ${x.name}`,
                      }))}
                      value={formData.inventory_id?.toString()}
                      onChange={v => updateForm('inventory_id', Number(v))}
                    />
                  </div>
                )}
                <div className='mb-17'>
                  <Input
                    label={translate(lang, 'Baskets capacity')}
                    type='number'
                    value={numToStr(formData.basket_count)}
                    onChange={e => updateForm('basket_count', e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className='mb-17'>
                <Input
                  label={translate(lang, 'Tank area')}
                  type='number'
                  unit='m²'
                  value={numToStr(formData.tank_area)}
                  onChange={e => updateForm('tank_area', e.target.value)}
                />
              </div>
            )}
            <div className='pb-24'>
              <Dropdown
                label={transUnit(lang, 'line_group', curFarm?.structure)}
                options={groupOptions}
                value={formData.line_group_id?.toString() ?? '0'}
                onChange={v =>
                  updateForm('line_group_id', v === '0' ? null : Number(v))
                }
              />
            </div>
            {curFarm?.structure === 'RAFT' ? (
              <SectionLocationForm
                className='mb-10'
                points={formData.points ?? []}
                onChange={pts => setFormData(prv => ({ ...prv, points: pts }))}
                farmData={curFarm}
              />
            ) : (
              <div className='pb-10'>
                {[0, 1].map(x => (
                  <div key={x} className='pb-17 w-100 d-flex justify-between'>
                    <div className='mr-17'>
                      <Input
                        label={translate(
                          lang,
                          x === 0
                            ? '_start_location_latitude'
                            : '_end_location_latitude',
                        )}
                        placeholder={translate(lang, 'Latitude')}
                        type='number'
                        value={
                          formData.points && formData.points.length > x
                            ? numToStr(formData.points[x].lat)
                            : ''
                        }
                        onChange={e =>
                          handleChangePoints(x, 'lat', e.target.value)
                        }
                      />
                    </div>
                    <div className='ml-17 mr-17'>
                      <Input
                        label={translate(lang, 'Longitude')}
                        className='mr-17'
                        type='number'
                        value={
                          formData.points && formData.points.length > x
                            ? numToStr(formData.points[x].lng)
                            : ''
                        }
                        onChange={e =>
                          handleChangePoints(x, 'lng', e.target.value)
                        }
                      />
                    </div>
                    <div className='ml-17 d-flex align-end'>
                      <Button
                        width='wide'
                        size={1}
                        type='fill'
                        color='blue'
                        onClick={() => setVisibleMap(true)}
                      >
                        {translate(lang, 'Pickup')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className='mt-24 mb-32 line-button d-flex justify-end align-center'>
              <Link to={`/farms/${curFarm?.id}`}>
                <Button
                  width={width < 769 ? 'wide' : 'small'}
                  size={1}
                  type='transparent'
                  color='blue'
                >
                  {translate(lang, 'Cancel')}
                </Button>
              </Link>
              <Button
                width={width < 769 ? 'wide' : 'small'}
                size={1}
                type='fill'
                color='blue'
                className='ml-16'
                onClick={handleConfirmData}
                disabled={disabled}
              >
                {translate(lang, 'Confirm')}
              </Button>
            </div>
            {visibleMap && curFarm && (
              <LineLocationModal
                visible={true}
                farmID={curFarm?.id}
                points={
                  formData.points
                    ? (formData.points.filter(
                        x => !!x.lat && !!x.lat,
                      ) as ILocation[])
                    : undefined
                }
                onConfirm={points => {
                  updateForm('points', points);
                  setVisibleMap(false);
                }}
                onCancel={() => setVisibleMap(false)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLines;
